import * as React from 'react';
import {
  Popover,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Link,
  BadgeOrigin,
  Stack
} from '@mui/material';
import { AppContext, Links } from '../../model';
import { useState } from 'react';
import { BadgedAvatar } from '../badged-avatar/badged-avatar';
import Done from '@mui/icons-material/Done';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

export interface UserMenuProps {
  context?: AppContext;
  links?: Links;
  onLoading?: (loading: boolean) => void;
}

const TrustedBadgeData = (color: string) => {
  return {
    badgeContent: (
      <Box title="Trusted">
        <Done sx={{ width: 13, height: 13 }} />
      </Box>
    ),
    badgeBackgroundColor: color,
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' } as BadgeOrigin
  };
};

export function UserMenu(props: UserMenuProps) {
  const { context, links } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = () => {
    setAnchorEl(null);
    if (props.onLoading) {
      props.onLoading(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const trustedUserData =
    context?.user?.roles && context?.user?.roles.includes('TRUSTED')
      ? TrustedBadgeData(context.actor ? 'res.main' : 'info.main')
      : undefined;

  if (!context?.user || !links?.login) {
    return <></>;
  }

  return (
    <Box>
      <Box sx={{ pl: 1 }}>
        {context && context.user && context.user.authenticated ? (
          <IconButton
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            {
              <BadgedAvatar
                sx={{
                  fontSize: 16,
                  ...(context.actor && { borderWidth: 2, borderColor: 'res.main', borderStyle: 'solid' })
                }}
                alt={context.user.fullName}
                src={context.user.image}
                badgeSize="small"
                {...trustedUserData}
              >
                {context.user.initials}
              </BadgedAvatar>
            }
          </IconButton>
        ) : (
          <Button href={links?.login} onClick={handleMenuClick} variant="contained" color="secondary">
            Sign In
          </Button>
        )}
      </Box>
      {context && context.user && context.user?.authenticated && (
        <Popover
          PaperProps={{ sx: { width: 288 } }}
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box sx={{ my: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <BadgedAvatar
              sx={{
                width: 72,
                height: 72,
                fontSize: 42,
                ...(context.actor && { borderWidth: 2, borderColor: 'res.main', borderStyle: 'solid' })
              }}
              alt={context?.user?.fullName}
              src={context?.user?.image}
              badgeSize="large"
              {...trustedUserData}
            >
              {context?.user?.initials}
            </BadgedAvatar>
          </Box>
          {context?.user?.fullName && (
            <Typography
              sx={{ mb: 1, textAlign: 'center' }}
              variant="subtitle1"
              noWrap
              title={context.user.fullName}
              component="div"
            >
              {context.user.fullName}
            </Typography>
          )}

          {context?.user?.iata && (
            <Typography sx={{ mb: 1, textAlign: 'center' }} variant="body2" gutterBottom component="div">
              IATA# {context.user.iata}
            </Typography>
          )}
          {context.user.loginName && context.user.loginName !== '' ? (
            <Typography
              sx={{ mb: 2, textAlign: 'center' }}
              variant="caption"
              noWrap
              title={context.user.loginName}
              gutterBottom
              component="div"
            >
              {context.user.loginName}
            </Typography>
          ) : <>{context.user.email && (
            <Typography
              sx={{ mb: 2, textAlign: 'center' }}
              variant="caption"
              noWrap
              title={context.user.email}
              gutterBottom
              component="div"
            >
              {context.user.email}
            </Typography>
          )} </>
          }
          {context?.actor?.fullName && (
            <Stack direction={'row'} spacing={1} justifyContent={'center'} sx={{ py: 1 }}>
              <SupportAgentIcon sx={{ color: 'res.main' }} />
              <Typography
                sx={{ mb: 1, textAlign: 'center' }}
                variant="subtitle1"
                color="res.main"
                title={context.actor.fullName}
                component="div"
              >
                {context.actor.fullName}
              </Typography>
            </Stack>
          )}
          {links?.user && (
            <>
              <Divider />
              <List sx={{ py: 0 }}>
                {links?.user?.map((link, index) => (
                  <ListItemButton
                    key={link.id}
                    component={Link}
                    href={link.path}
                    divider={index == (links && links?.user && links?.user.length - 1)}
                    sx={{ px: 4 }}
                    onClick={handleMenuClick}
                  >
                    <ListItemText primary={link.text} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          {links.logout && (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Button href={links?.logout} variant="outlined" onClick={handleMenuClick} color="secondary">
                Sign Out
              </Button>
            </Box>
          )}
        </Popover>
      )}
    </Box>
  );
}
